/**
 * Prompt Test panel for one payer.
 *
 * Collects an order id, drug names, and a letter PDF, then runs the real
 * auth-letter extraction agent for this payer and renders the result via
 * {@link ExtractionResult}.
 * @module PromptTestPanel
 */
import { useState } from "react";
import {
  AuthLetterUploadExtraction,
  runAuthLetterUploadExtraction,
} from "../../../../../api/services/authLetterAgentTestService";
import { AGENTIC_AUTH_ON_FILE_ORG_TABS } from "../../../../../constants/agenticAuthOnFileOrgTabs";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import ExtractionResult from "./ExtractionResult";

interface Props {
  payerName: string;
  onClose: () => void;
}

/**
 * Renders the test form and extraction result for one payer's prompt.
 *
 * @param props.payerName - Payer whose prompt is tested
 * @param props.onClose - Called when the panel is closed
 */
const PromptTestPanel = ({ payerName, onClose }: Props) => {
  const orgId = getOrgIdForFetchExternalWorklist() || "";
  const orgLabel =
    AGENTIC_AUTH_ON_FILE_ORG_TABS.find((t) => t.id === orgId)?.label ?? orgId;
  const [orderId, setOrderId] = useState("");
  const [drugNames, setDrugNames] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuthLetterUploadExtraction | null>(null);

  const run = async () => {
    if (!file) {
      setError("Upload the auth letter PDF first.");
      return;
    }
    if (!orderId.trim()) {
      setError("Enter the order id.");
      return;
    }
    const medicineNames = drugNames
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (medicineNames.length === 0) {
      setError("Enter at least one drug name from the order.");
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await runAuthLetterUploadExtraction({
          payerName,
          orderId: orderId.trim(),
          orgId,
          medicineNames,
          file,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Test run failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-tertiaryBlue-10 bg-tertiaryBlue-12 p-4">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-bold text-primaryGray-1">
          Test prompt — {payerName}
        </span>
        <button
          onClick={onClose}
          className="ml-auto text-tiny font-semibold text-primaryGray-6 hover:text-primaryGray-1"
        >
          Close
        </button>
      </div>
      <div className="text-xs text-primaryGray-6">
        Runs the real extraction agent on a letter for one order and shows what
        was extracted.
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <label className="text-tiny font-semibold text-primaryGray-1">
          Order ID
        </label>
        <input
          className="rounded border border-primaryGray-14 bg-white px-3 py-2 text-sm text-primaryGray-1 focus:border-primaryGray-1 focus:outline-none"
          placeholder="e.g. 3f8c…"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
        />
        <span className="text-tiny text-primaryGray-6">
          Organization: <span className="font-semibold">{orgLabel || "—"}</span>{" "}
          (from your login)
        </span>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <label className="text-tiny font-semibold text-primaryGray-1">
          Drug names on the order
        </label>
        <textarea
          className="min-h-[60px] w-full resize-y rounded border border-primaryGray-14 bg-white p-2 text-sm text-primaryGray-1 focus:border-primaryGray-1 focus:outline-none"
          placeholder="One per line, or comma-separated (e.g. Keytruda, Opdivo)"
          value={drugNames}
          onChange={(e) => setDrugNames(e.target.value)}
        />
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <label className="text-tiny font-semibold text-primaryGray-1">
          Auth letter PDF
        </label>
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-xs text-primaryGray-6"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={run}
          disabled={running}
          className="rounded border border-gray-800 bg-gray-800 px-4 py-1.5 text-tiny font-semibold text-white hover:bg-gray-700 disabled:opacity-50"
        >
          {running ? "Running…" : "Run test"}
        </button>
      </div>

      {error && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-600">
          {error}
        </div>
      )}

      {result && <ExtractionResult result={result} />}
    </div>
  );
};

export default PromptTestPanel;
